var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useState, useEffect, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { isPublisher } from "../utils/permissionsUtils";
import { usePermissions } from "./usePermissions";
import { usePublisherAutoFill } from "./usePublisherAutoFill";
import { useDateInputValidity } from "./useDateInputValidity";
import { DEFAULT_PRODUCT_ACCESSIBILITY } from "../utils/filterDefaults";
import { appendSelectionParams, sameSelection, selectionFromParams, toggleRequiredSelection, } from "../utils/requiredSelection";
import { appendDateRangeParams, areDateRangesValid, dateRangeFiltersFromParams, EMPTY_DATE_RANGE_FILTERS, hasActiveDateRangeFilter, } from "../utils/dateRangeFilters";
var INITIAL_FILTERS = __assign({ id: "", productNumber: "", publisher: "", contributor: "", name: "", accessibility: DEFAULT_PRODUCT_ACCESSIBILITY, publisherId: "" }, EMPTY_DATE_RANGE_FILTERS);
var FILTER_PARAMS = Object.keys(INITIAL_FILTERS);
/**
 * The applied filters as the URL holds them.
 *
 * @param search `location.search` of the list
 * @param publicOnlyView the public-only view has no accessibility filter, so a
 *   stale param is ignored
 */
var filtersFromSearch = function (search, publicOnlyView) {
    var queryParams = new URLSearchParams(search);
    return __assign({ id: queryParams.get("id") || "", productNumber: queryParams.get("productNumber") || "", publisher: queryParams.get("publisher") || "", contributor: queryParams.get("contributor") || "", name: queryParams.get("name") || "", 
        // No accessibility param in the URL falls back to the default (both options).
        accessibility: publicOnlyView
            ? DEFAULT_PRODUCT_ACCESSIBILITY
            : selectionFromParams(queryParams, "accessibility", DEFAULT_PRODUCT_ACCESSIBILITY), publisherId: queryParams.get("publisherId") || "" }, dateRangeFiltersFromParams(queryParams));
};
/** Filter params only the agency may send (see `filtersFromSearch`). */
var hasAgencyOnlyParam = function (search) {
    return new URLSearchParams(search).has("accessibility");
};
var hasAnyFilterParam = function (search) {
    var params = new URLSearchParams(search);
    return FILTER_PARAMS.some(function (key) { return params.has(key); });
};
export var useProductFilters = function () {
    var history = useHistory();
    var location = useLocation();
    // Agency-only filters are dropped for the public-only view and while the
    // roles could not be loaded (the backend would refuse them with 403 -92).
    var _a = usePermissions(), searchAsPublicOnly = _a.searchAsPublicOnly, activeRoles = _a.activeRoles, activeRolesError = _a.activeRolesError;
    var initialFilters = INITIAL_FILTERS;
    // Initialised from the URL in the first render: filters patched in by an
    // effect afterwards would change the search and send it twice.
    var _b = useState(function () {
        return filtersFromSearch(location.search, searchAsPublicOnly);
    }), filters = _b[0], setFilters = _b[1];
    var _c = useState(function () { return filters; }), appliedFilters = _c[0], setAppliedFilters = _c[1];
    var _d = useState(""), publisherQuery = _d[0], setPublisherQuery = _d[1];
    var dateInputs = useDateInputValidity();
    var resetDateInputs = dateInputs.resetInputs;
    var updateUrlWithFilters = useCallback(function (newFilters) {
        var params = new URLSearchParams();
        if (newFilters.id)
            params.set("id", newFilters.id);
        if (newFilters.productNumber)
            params.set("productNumber", newFilters.productNumber);
        if (newFilters.publisher)
            params.set("publisher", newFilters.publisher);
        if (newFilters.contributor)
            params.set("contributor", newFilters.contributor);
        if (newFilters.name)
            params.set("name", newFilters.name);
        if (newFilters.publisherId)
            params.set("publisherId", newFilters.publisherId);
        appendSelectionParams(params, "accessibility", newFilters.accessibility, DEFAULT_PRODUCT_ACCESSIBILITY);
        appendDateRangeParams(params, newFilters);
        history.push({ search: params.toString() });
    }, [history]);
    // A list opened without filter params shows a publisher user their own products.
    var autoFill = usePublisherAutoFill({
        appliesTo: isPublisher,
        hasFilterParams: hasAnyFilterParam,
        canAutoFill: function () { return !filters.publisherId; },
        onAutoFill: function (publisherSourceId) {
            var autoFilledFilters = __assign(__assign({}, INITIAL_FILTERS), { publisherId: publisherSourceId });
            autoFill.cancel();
            setFilters(autoFilledFilters);
            setAppliedFilters(autoFilledFilters);
            updateUrlWithFilters(autoFilledFilters);
        },
    });
    var handleFilterChange = function (field, value) {
        var _a;
        var newFilters = __assign(__assign({}, filters), (_a = {}, _a[field] = value, _a));
        setFilters(newFilters);
    };
    var handleAccessibilityChange = function (accessibility) {
        var newAccessibility = toggleRequiredSelection(filters.accessibility, accessibility);
        if (newAccessibility === filters.accessibility)
            return;
        handleFilterChange("accessibility", newAccessibility);
    };
    // Od after Do, or a picker holding text that is not a valid date.
    var hasInvalidDateRange = !areDateRangesValid(filters) || dateInputs.hasInvalidInput;
    var handleApplyFilters = function () {
        // An Od after Do is reported in the filter pane and never sent.
        if (hasInvalidDateRange)
            return;
        var newFilters = __assign(__assign({}, filters), { id: filters.id.trim() });
        autoFill.cancel();
        setFilters(newFilters);
        setAppliedFilters(newFilters);
        updateUrlWithFilters(newFilters);
    };
    var handlePublisherInput = function (inputValue) {
        if (inputValue !== publisherQuery) {
            setPublisherQuery(inputValue);
        }
    };
    var handleClearFilters = function () {
        var clearedFilters = initialFilters;
        autoFill.cancel();
        setFilters(clearedFilters);
        setAppliedFilters(clearedFilters);
        setPublisherQuery("");
        resetDateInputs();
        history.push({ search: "" });
    };
    var isAnyFilterActive = function () {
        return (appliedFilters.id.length > 0 ||
            appliedFilters.productNumber.length > 0 ||
            appliedFilters.publisher.length > 0 ||
            appliedFilters.contributor.length > 0 ||
            appliedFilters.name.length > 0 ||
            !sameSelection(appliedFilters.accessibility, DEFAULT_PRODUCT_ACCESSIBILITY) ||
            appliedFilters.publisherId.length > 0 ||
            hasActiveDateRangeFilter(appliedFilters));
    };
    var hasUnappliedChanges = function () {
        return JSON.stringify(filters) !== JSON.stringify(appliedFilters);
    };
    var buildQuery = function (filterValues, selectedPublisher) {
        if (filterValues === void 0) { filterValues = appliedFilters; }
        var queryParts = [];
        if (filterValues.productNumber.trim()) {
            var productNumberTerm = filterValues.productNumber.trim();
            queryParts.push("ownerId==\"*".concat(productNumberTerm, "*\""));
        }
        if (filterValues.publisher.trim()) {
            var publisherTerm = filterValues.publisher.trim();
            queryParts.push("title.owner.name==\"*".concat(publisherTerm, "*\""));
        }
        if (filterValues.contributor.trim()) {
            var contributorTerm = filterValues.contributor.trim();
            queryParts.push("(title.titleContributions.lastName==\"*".concat(contributorTerm, "*\" or title.titleContributions.name==\"*").concat(contributorTerm, "*\")"));
        }
        if (filterValues.name.trim()) {
            var titleTerm = filterValues.name.trim();
            queryParts.push("(title.names==\"*".concat(titleTerm, "*\")"));
        }
        if (filterValues.accessibility.length > 0) {
            var accessibilityQueries = filterValues.accessibility
                .map(function (accessibility) { return "accessibility==\"".concat(accessibility, "\""); })
                .join(" or ");
            queryParts.push("(".concat(accessibilityQueries, ")"));
        }
        if (filterValues.publisherId.trim()) {
            var publisherId = (selectedPublisher === null || selectedPublisher === void 0 ? void 0 : selectedPublisher.sourceId) || filterValues.publisherId.trim();
            queryParts.push("publisher=\"".concat(publisherId, "\""));
        }
        return queryParts.length > 0 ? queryParts.join(" and ") : "";
    };
    useEffect(function () {
        // Also re-sanitises the applied filters once the roles are known.
        var newFilters = filtersFromSearch(location.search, searchAsPublicOnly);
        if (JSON.stringify(newFilters) !== JSON.stringify(appliedFilters)) {
            setFilters(newFilters);
            setAppliedFilters(newFilters);
        }
    }, [location.search, appliedFilters, searchAsPublicOnly]);
    // Back/forward or a pasted URL replaces the draft: drop typed text the pickers still hold.
    useEffect(function () {
        resetDateInputs();
    }, [location.search, resetDateInputs]);
    /** False until the publisher auto-fill is decided — a search before it would be thrown away. */
    var filtersReady = !autoFill.isPending &&
        // an agency-only param from the URL waits for the roles that decide it
        !(activeRoles === undefined &&
            !activeRolesError &&
            hasAgencyOnlyParam(location.search));
    return {
        filters: filters,
        appliedFilters: appliedFilters,
        filtersReady: filtersReady,
        /** true when the logged-in user or their roles could not be loaded */
        currentUserLoadFailed: autoFill.isError || activeRolesError,
        publisherQuery: publisherQuery,
        handleFilterChange: handleFilterChange,
        handleAccessibilityChange: handleAccessibilityChange,
        handlePublisherInput: handlePublisherInput,
        handleApplyFilters: handleApplyFilters,
        handleClearFilters: handleClearFilters,
        isAnyFilterActive: isAnyFilterActive,
        hasUnappliedChanges: hasUnappliedChanges,
        hasInvalidDateRange: hasInvalidDateRange,
        dateInputs: dateInputs,
        buildQuery: buildQuery,
    };
};
